/**
 * String case-conversion helpers. Pure-leaf module: NO value imports.
 */

/**
 * The camel/Pascal LOWER-TO-UPPER word boundary: a lowercase letter or digit (group 1) immediately followed by an
 * uppercase letter (group 2). Consumers splice a separator between the two groups (`'$1-$2'`) to break `fooBar` into
 * `foo|Bar`.
 *
 * A SOURCE STRING rather than a shared `/g` RegExp object, per the `const/regexes.ts` flag discipline: a global
 * instance carries mutable `lastIndex` ON THE OBJECT, and while `String#replace` resets it per call, an `.exec` or
 * `.test` consumer would resume mid-string against a sibling's position. Each consumer builds its own:
 * `new RegExp(CAMEL_LOWER_UPPER_BOUNDARY_SOURCE, 'g')`.
 *
 * This is the FIRST of the two boundaries {@link toKebabCase} applies and is NOT sufficient alone: the acronym-to-word
 * boundary below it (`([A-Z])([A-Z][a-z])`) is what keeps `HTTPServer` from becoming `h-t-t-p-server`. That second
 * shape stays an inline literal because it is spelled in this file ONLY -- a single-file literal is the recorded,
 * accepted boundary of `QUAL-CROSS-FILE-REGEX-CONST-DUP-001`, which fires at two or more files.
 * Adopters: `QUAL-TYPES-FAMILY-STEM-BUCKET-001` names its own `CAMEL_BOUNDARY_RE`; the three money matchers reach the
 * boundary through {@link identifierSegments} below instead.
 */
export const CAMEL_LOWER_UPPER_BOUNDARY_SOURCE = '([a-z0-9])([A-Z])';

/** Module-private compiled instance for {@link identifierSegments}. Private and `.replace`-only for the same reason
 *  {@link CAMEL_LOWER_UPPER_BOUNDARY_RE} is: `String#replace` resets `lastIndex` per call and no other holder exists.
 *  A SECOND instance rather than a reuse of the kebab converter's, because the two must stay independently
 *  retunable -- the converter splices a `-` and this one splices a space it then splits on. */
const IDENTIFIER_SEGMENT_BOUNDARY_RE = new RegExp(CAMEL_LOWER_UPPER_BOUNDARY_SOURCE, 'g');

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the lowercased camelCase + snake_case + `$` SEGMENTS of one
 * identifier -- `current_balance` and `currentBalance` both yield `['current', 'balance']`, `total_credits$raw` yields
 * `['total', 'credits', 'raw']`, and an empty or all-separator input yields `[]`.
 *
 * DISTINCT FROM {@link toKebabCase}, which is a mixed-input three-pass CONVERTER with a lowercase short-circuit, a
 * SCREAMING_SNAKE path and the acronym-to-word boundary. This one deliberately applies only the lower-to-upper
 * boundary and returns the PARTS rather than a joined string: its consumers ask set questions of the segments
 * (`segments.includes('balance')`, `segments.at(-1)`), which a joined string cannot answer. Because it skips the
 * acronym boundary, `HTTPAmount` segments as `['httpamount']` -- correct for these consumers, whose vocabularies are
 * money nouns, and the reason this is not spelled as a `toKebabCase(...).split('-')`.
 *
 * Homed in `const/` so the three money rule matchers (`QUAL-DOLLAR-BALANCE-RAW-COMPARE-001`,
 * `QUAL-DOLLAR-MONEY-RAW-ARITHMETIC-001`, `QUAL-LEDGER-CORE-POSTING-ROUNDED-REAL-001`) share one segmentation: they
 * carried three copies of this body, so the three could disagree about what a segment IS while their `message` and
 * `fix` fields cross-cite each other's cures.
 */
export function identifierSegments(identifier: string): string[] {
	return identifier
		.replace(IDENTIFIER_SEGMENT_BOUNDARY_RE, '$1 $2')
		.split(/[\s_$]+/)
		.filter((segment) => segment !== '')
		.map((segment) => segment.toLowerCase());
}

/** Module-private compiled instance of {@link CAMEL_LOWER_UPPER_BOUNDARY_SOURCE} for {@link toKebabCase}, so the
 *  converter pays no per-call `RegExp` construction. Private and `.replace`-only, so its `lastIndex` is unobservable:
 *  `String#replace` resets it on every call and no other holder exists. */
const CAMEL_LOWER_UPPER_BOUNDARY_RE = new RegExp(CAMEL_LOWER_UPPER_BOUNDARY_SOURCE, 'g');

/** The splice {@link toKebabCase} makes at BOTH of its boundaries: the two captured halves, joined by a hyphen. */
const KEBAB_BOUNDARY_SPLICE = '$1-$2';

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the SNAKE-TO-KEBAB transform alone -- every underscore a hyphen,
 * the whole lower-cased, with no short-circuit and no word-boundary pass. It is the underscore-only path
 * {@link toKebabCase} takes for a SCREAMING_SNAKE input, and the whole of what a snake-only call site wants (a
 * snake_case table name becoming a kebab leaf basename, `const/d1-row-interface-source.ts`).
 */
export function snakeToKebab(snake: string): string {
	return snake.replace(/_/g, '-').toLowerCase();
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic converter homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. MIXED-INPUT converter, which is why it is
 * three passes and not one: an already-kebab/lowercase string short-circuits unchanged, a SCREAMING_SNAKE string
 * takes the underscore-only path ({@link snakeToKebab}), and anything else runs the camel/Pascal boundary splits
 * (lower-to-upper first, then the acronym-to-word boundary, so `HTTPServer` becomes `http-server` rather than
 * `h-t-t-p-server`).
 *
 * NOT a general snake-to-kebab converter: a pure `snake_case` input already matches the lowercase short-circuit
 * only when it carries no underscores, so a snake-only call site wants {@link snakeToKebab} rather than this. Keep
 * those distinct -- collapsing them silently changes which inputs pass through untouched.
 */
export function toKebabCase(str: string): string {
	if (/^[a-z0-9.-]+$/.test(str)) return str;
	if (/^[A-Z_]+$/.test(str)) return snakeToKebab(str);
	return str
		.replace(CAMEL_LOWER_UPPER_BOUNDARY_RE, KEBAB_BOUNDARY_SPLICE)
		.replace(/([A-Z])([A-Z][a-z])/g, KEBAB_BOUNDARY_SPLICE)
		.replace(/_/g, '-')
		.toLowerCase();
}

/** Module-private compiled instance for {@link upperSnake}: private and `.replace`-only, so its `lastIndex` is
 *  unobservable for the same reason every other instance in this file is. A THIRD instance rather than a reuse, on the
 *  same ground the two above are separate -- each consumer splices its own separator and must stay retunable. */
const UPPER_SNAKE_BOUNDARY_RE = new RegExp(CAMEL_LOWER_UPPER_BOUNDARY_SOURCE, 'g');

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): UPPER_SNAKE of an identifier or a rendered VALUE token, so
 * `selectionAria` plus `pressed` compose to `SELECTION_ARIA_PRESSED` and `Days to Close` yields `DAYS_TO_CLOSE`.
 *
 * TWO PASSES, and the second is what admits multi-word copy: with the camel split alone, `Entry Type` composed
 * `ENTRY TYPE` -- a space inside an identifier, which no `export const` can declare -- so every value carrying a
 * space, an ampersand or a hyphen was an unconditional miss. The pass is strictly ADDITIVE: it changes no result that
 * was already a legal identifier.
 *
 * Homed here rather than in the matcher that composed it, because TWO compliance rules now read the same composition:
 * `QUAL-JSX-ATTR-CONST-ADOPTION-001` composes the candidate const name it looks up, and `QUAL-CONST-GROUP-001` asks
 * whether a flat scalar IS such a candidate before it demands a fold that would delete it from that index.
 */
export function upperSnake(token: string): string {
	return token
		.replace(UPPER_SNAKE_BOUNDARY_RE, '$1_$2')
		.toUpperCase()
		.replace(/[^A-Z0-9]+/g, '_')
		.replace(/^_|_$/g, '');
}

/** Module-private compiled instance for {@link toCamelCase}: private and `.replace`-only, so its `lastIndex` is
 *  unobservable for the same reason the two boundary instances above are. */
const SNAKE_SEGMENT_HEAD_RE = /_([a-z0-9])/g;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the `String#replace` callback of every separator-to-camel fold --
 * the captured segment head (group 1) upper-cased, the separator matched around it dropped. {@link toCamelCase} folds
 * snake (`_x`) and `conventionExportName` (`const/flow-convention-naming.ts`) folds kebab (`-x`); the PATTERN is each
 * caller's decision, the head rule is this one.
 */
export function upperCaseSegmentHead(_match: string, head: string): string {
	return head.toUpperCase();
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): `snake_case` -> `camelCase`. Each `_` plus the alphanumeric that
 * follows it collapses to that character upper-cased; everything else passes through, so an already-camel input and a
 * SCREAMING_SNAKE input both return unchanged (no lowercase segment head to fold).
 *
 * THE DIGIT IN THE CHARACTER CLASS IS THE ADJUDICATION, not a copy-paste widening. Three bodies of this existed --
 * camelCaseOf (`QUAL-LEDGER-CORE-POSTING-ROUNDED-REAL-001`; deleted by the fold), `identifierFormsFor` (`QUAL-FEDERA-ATTR-KEY-RAW-LITERAL-001`)
 * and caseVariants (`utils/desk/handler-exec-template-drill.ts`) -- and they DISAGREED: the money matcher folded
 * `_2`, the other two left it, so `total_2x` was one identifier to one reader and two to another. `[a-z0-9]` wins
 * because it is what TypeScript authors actually write for a digit-bearing column, and adopting it costs the other two
 * nothing measurable: `const/telemetry-federa-attrs.ts` declares no key carrying `_<digit>` at all, and the desk drill
 * returns a deduped variant SET probed with `in`, where an extra spelling can only add a hit.
 *
 * SEPARATE from {@link toPascalCase} deliberately, on the same grounds that one is separate from {@link toKebabCase}:
 * this one is snake-ONLY and separator-PRESERVING (a `-` or `.` survives untouched), where the Pascal converter is
 * separator-blind and splits on every non-alphanumeric run. A call site wanting kebab input wants that one.
 */
export function toCamelCase(value: string): string {
	return value.replace(SNAKE_SEGMENT_HEAD_RE, upperCaseSegmentHead);
}

/** Module-private compiled instance for {@link humanizeSnakeCase}: private and `.replace`-only, so its `lastIndex` is
 *  unobservable for the same reason every other instance in this file is. */
const SNAKE_SEPARATOR_RE = /_/g;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): `snake_case` -> spaced display text. Every `_` becomes a space and
 * nothing else changes -- no casing pass, so `activity_type` yields `activity type`, not `Activity Type`. A blank,
 * whitespace-only or nullish input yields `''`.
 *
 * HOMED ON THE `const/` FLOOR, and it must stay there: a `utils/` home is UNREACHABLE from three of its consumers --
 * `const/title-instrument-labels.ts` (the missing-label fallback), `utils/title/verdict-prompt/role-section.ts` and
 * `utils/compliance/rule-helpers/const-inline-raw-line-shapes.ts` -- because `const/` cannot import `utils/` at all
 * and a `utils/` leaf cannot import a `utils/` sibling (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`). The `const/` floor
 * is the one home those three plus the `components/`, `mfe/` and `spa/` consumers all reach, which is what makes ONE
 * spelling adoptable everywhere.
 *
 * SEPARATE from {@link toKebabCase} and {@link toPascalCase}, which are CONVERTERS with short-circuits and
 * separator-blind splits. This one is a single display transform over one separator and must not gain their passes:
 * its output is read by humans, not re-parsed.
 *
 * The blank guard is spelled inline rather than through `isNonEmptyString` (`@federa/const/string-guards`) to keep
 * this module's declared no-value-imports purity; it is the same trim-then-length test that guard applies.
 *
 * PAIRED FORBID-RULE: the `humanizeSnakeCase` row of `SSOT_IDIOM_MANIFEST` (`const/ssot-idiom-index.ts`; the row is in
 * `const/ssot-idiom-text.rows.ts`), consumed by `QUAL-SSOT-IDIOM-HAND-ROLLED-001`. It anchors on the SPACE
 * replacement specifically, because the same `/_/g` source with a `'-'` replacement is {@link toKebabCase}'s
 * SCREAMING_SNAKE pass and a different decision.
 */
export function humanizeSnakeCase(str: string): string {
	if (!str || str.trim().length === 0) return '';
	return str.replace(SNAKE_SEPARATOR_RE, ' ');
}

/**
 * Any DELIMITED stem -- kebab, snake, dotted, whitespace-separated -- to PascalCase: split on every non-alphanumeric
 * run, drop empties, upper-case each segment's first character, join. `fund-buying-wire` and `user_wishlist.add` both
 * yield `FundBuyingWire` / `UserWishlistAdd`; an ALREADY-Pascal input passes through unchanged, and an all-delimiter
 * input yields `''`.
 *
 * A SEPARATE EXPORT, NOT AN ARM OF {@link toKebabCase}, and that is an adjudication rather than an omission. The
 * kebab converter is a MIXED-INPUT three-pass converter with a lowercase short-circuit and a SCREAMING_SNAKE path,
 * whose own docstring instructs a snake-only call site to keep its own `replace`. This one is single-pass and
 * separator-blind by design: it must treat `-`, `_`, `.` and space identically so one home can serve the wrangler
 * env-interface derivation (kebab package stems), the D1 row-type generator (snake table names) and the flow
 * type-name emitters (dotted flow ids). Giving the two converters one body would mean either teaching this one the
 * short-circuits -- which would silently change which inputs pass through untouched -- or dropping them from that
 * one. They share a family and a file, not an implementation.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic converter homed in `const/` so pure `utils/` leaves can
 * value-import it -- `utils/linting/env-interface.ts`, `utils/type-definitions/generate-d1-row-types.ts` and the
 * three `utils/type-definitions/flow-*-types-*.ts` emitters cannot import a utils sibling, and before this home
 * existed they carried three separate bodies of it. `QUAL-MIGRATION-DOMAIN-TYPE-001` was a FOURTH body (a private
 * snakeToPascal, deleted by that adoption) and now reads through this one: it derives the type name it hunts from a D1 table name, the same
 * (snake stem -> Pascal type name) decision the row-type generator makes, so the two must not be able to disagree.
 */
export function toPascalCase(value: string): string {
	return value
		.split(/[^A-Za-z0-9]+/)
		.filter(Boolean)
		.map(upperFirst)
		.join('');
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): one word with its FIRST character upper-cased and every other
 * character left exactly as it stands -- the segment step {@link toPascalCase} applies, and the `(word) =>
 * word.charAt(0).toUpperCase() + word.slice(1)` lambda the display labels, the flow-name derivations and the rule
 * matchers had each typed. No lower-casing of the rest (a title-case label that wants it lower-cases first) and no
 * blank guard (`capitalizeFirst`, `utils/strings/transform.ts`, answers '' for a blank input; this answers the input).
 */
export function upperFirst(word: string): string {
	return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): whether `text` opens on an upper-case letter -- a first character
 * whose lower-case form differs from itself, so a digit, a symbol and the empty string all answer false. The
 * `first !== first.toLowerCase()` test the JSX element classifier (a PascalCase component, not a DOM tag) and the flow
 * trigger sentence check (a `soThat` clause that must continue, not open, a sentence) each spelled.
 */
export function startsUpperCase(text: string): boolean {
	const first = text.charAt(0);
	return first !== first.toLowerCase();
}
